/**
 * attachment-preview.ts - Resolve como um anexo deve ser aberto
 *
 * Centraliza a decisão entre PREVIEW INTERNO (ImageLightbox — que suporta
 * imagem, vídeo e documento via iframe) e DOWNLOAD/abertura externa para
 * formatos que o navegador não renderiza inline (Word, Excel, zip etc.).
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-21 v1.0.0 - Criação inicial
 */

/** Tipos aceitos pelo ImageLightbox */
export type PreviewMediaType = 'image' | 'video' | 'document';

/**
 * Resolve o tipo de mídia para o preview interno.
 * Retorna `null` quando o arquivo não pode ser exibido inline.
 *
 * @param fileType - Valor do enum file_type (image | pdf | document | spreadsheet | other)
 * @param mimeType - MIME real do arquivo (quando disponível, tem prioridade)
 */
export function resolvePreviewMediaType(
  fileType?: string | null,
  mimeType?: string | null,
): PreviewMediaType | null {
  const mime = (mimeType || '').toLowerCase();

  // MIME é a fonte mais confiável
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'application/pdf') return 'document';
  if (mime.startsWith('text/')) return 'document';

  // Fallback pelo enum file_type
  switch (fileType) {
    case 'image':
      return 'image';
    case 'video':
      return 'video';
    case 'pdf':
      return 'document';
    default:
      return null;
  }
}

/** Indica se o anexo pode ser aberto no preview interno */
export function canPreviewInline(
  fileType?: string | null,
  mimeType?: string | null,
): boolean {
  return resolvePreviewMediaType(fileType, mimeType) !== null;
}

/**
 * Baixa um arquivo via fetch+blob (contorna limitação cross-origin do
 * atributo `download`). Usado quando o anexo não pode ser pré-visualizado.
 */
export async function downloadAttachmentFile(url: string, fileName: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}
